import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  ContentLayout,
  Form,
  FormField,
  Header,
  Input,
  SegmentedControl,
  SpaceBetween,
  Spinner,
  Textarea,
} from "@cloudscape-design/components";
import { apiFetch } from "./apiFetch";
import {
  emptyView,
  parseView,
  serializeView,
  type DetailWidget,
  type TableWidget,
  type TabEntry,
  type TabsWidget,
  type View,
  type ViewParam,
  type Widget,
} from "./dynamicPages/viewContract";
import { fetchContentTypes, type SchemaContentType } from "./dynamicPages/schemaApi";
import TableWidgetConfig, { emptyTableWidget } from "./dynamicPages/TableWidgetConfig";
import DetailWidgetConfig, { emptyDetailWidget } from "./dynamicPages/DetailWidgetConfig";
import { useDocumentTitle } from "./useDocumentTitle";

/**
 * Create/edit editor for a dynamic `page`. The raw view JSON STRING is the single source
 * of truth: the Builder is a convenience that parses that string into an ORDERED WIDGET
 * LIST and serialises back on exit. Switching JSON -> Builder parses and refuses to render
 * the form on a malformed/non-conforming view (keeping the user's text); switching
 * Builder -> JSON always serialises the widget list back into the textarea.
 *
 * The builder manages `widgets: Widget[]` directly:
 *  - 'Add widget' appends a Table or a Tabs widget.
 *  - Each widget is a card with a type header, its config area, Remove, and Move up/down
 *    reorder affordances.
 *  - A Table card hosts <TableWidgetConfig> (the reusable per-table form).
 *  - A Tabs card hosts an ordered add/remove/reorderable list of tabs; each tab is a title
 *    Input + a nested <TableWidgetConfig>. The builder exposes ONE level of Tabs (no
 *    Tabs-inside-a-tab); Tabs-in-Tabs remains valid JSON only.
 *
 * REORDER UX: CloudScape List's `renderItem` is designed for simple item rows (content +
 * actions), not large editable card bodies with nested inputs/selects/modals; its drag
 * handles would also compete with the form controls. Move up/down buttons are the cleaner,
 * CloudScape-only choice for reordering arbitrary editable cards, so they are used here.
 */

type Mode = "json" | "builder";

/** A fresh, empty tabs widget with a single blank table tab. */
function emptyTabsWidget(): TabsWidget {
  return { type: "tabs", tabs: [{ title: "", widget: emptyTableWidget() }] };
}

/**
 * Build a serializable View from builder state. Param names are TRIMMED; empty names and
 * duplicates (first occurrence wins) are dropped so distinct rows are never silently
 * merged into malformed JSON. `params` is OMITTED entirely when the cleaned list is empty.
 */
function buildViewFromBuilder(widgets: Widget[], params: ViewParam[]): View {
  const seen = new Set<string>();
  const cleanParams: ViewParam[] = [];
  for (const p of params) {
    const name = p.name.trim();
    if (name === "" || seen.has(name)) continue;
    seen.add(name);
    cleanParams.push({ name });
  }
  const view: View = { widgets };
  if (cleanParams.length > 0) view.params = cleanParams;
  return view;
}

export default function PageEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [title, setTitle] = useState("");
  useDocumentTitle(isEdit ? `Edit ${title || "Page"}` : "New Page");
  const [slug, setSlug] = useState("");
  const [viewJson, setViewJson] = useState<string>(() => serializeView(emptyView()));

  const [mode, setMode] = useState<Mode>("json");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [builderParseError, setBuilderParseError] = useState<string | null>(null);

  const [schema, setSchema] = useState<SchemaContentType[]>([]);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  // Builder state: the ordered widget list (only meaningful while mode === "builder").
  const [widgets, setWidgets] = useState<Widget[]>([]);
  // Builder state: the view-level URL parameters (only meaningful while mode === "builder").
  const [params, setParams] = useState<ViewParam[]>([]);

  // Load content-type schema once.
  useEffect(() => {
    let cancelled = false;
    fetchContentTypes()
      .then((cts) => {
        if (!cancelled) setSchema(cts);
      })
      .catch((e: Error) => {
        if (!cancelled) setSchemaError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the page in edit mode.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    setLoading(true);
    apiFetch(`/pages/${id}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed to load page (${res.status})`);
        const json = await res.json();
        const record = json?.data;
        if (!record) throw new Error("Page not found.");
        if (cancelled) return;
        setTitle(record.title ?? "");
        setSlug(record.slug ?? "");
        const raw =
          typeof record.view === "string"
            ? record.view
            : JSON.stringify(record.view ?? emptyView(), null, 2);
        setViewJson(raw);
      })
      .catch((e: Error) => {
        if (!cancelled) setLoadError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  // --- Mode switching -----------------------------------------------------

  // JSON -> Builder: parse the textarea into the widget list; on failure keep text + error.
  const enterBuilder = useCallback(() => {
    const parsed = parseView(viewJson);
    if (!parsed.ok) {
      setBuilderParseError(parsed.error);
      setMode("builder");
      return;
    }
    setBuilderParseError(null);
    // Deep-clone so builder edits never mutate the parsed object in place.
    setWidgets(JSON.parse(JSON.stringify(parsed.view.widgets)) as Widget[]);
    setParams(JSON.parse(JSON.stringify(parsed.view.params ?? [])) as ViewParam[]);
    setMode("builder");
  }, [viewJson]);

  // Builder -> JSON: serialise the current widget list back into the textarea.
  const enterJson = useCallback(() => {
    if (!builderParseError) {
      setViewJson(serializeView(buildViewFromBuilder(widgets, params)));
    }
    setMode("json");
  }, [builderParseError, widgets, params]);

  const onModeChange = useCallback(
    (next: Mode) => {
      if (next === mode) return;
      if (next === "builder") enterBuilder();
      else enterJson();
    },
    [mode, enterBuilder, enterJson]
  );

  // --- View-level parameter operations -----------------------------------

  // The distinct, trimmed param names currently defined — offered to each table's
  // param-filter param Select. Blank/duplicate rows are excluded (first occurrence wins).
  const paramNames = (() => {
    const seen = new Set<string>();
    const names: string[] = [];
    for (const p of params) {
      const name = p.name.trim();
      if (name === "" || seen.has(name)) continue;
      seen.add(name);
      names.push(name);
    }
    return names;
  })();

  // Inline duplicate check against OTHER rows (trimmed, non-empty) for error text.
  const isDuplicateParamName = (index: number): boolean => {
    const name = params[index].name.trim();
    if (name === "") return false;
    return params.some((p, i) => i !== index && p.name.trim() === name);
  };

  const addParam = () => setParams((prev) => [...prev, { name: "" }]);
  const removeParam = (index: number) => setParams((prev) => prev.filter((_, i) => i !== index));
  const setParamName = (index: number, name: string) =>
    setParams((prev) => prev.map((p, i) => (i === index ? { name } : p)));

  // --- Widget list operations --------------------------------------------

  const addTableWidget = () => setWidgets((prev) => [...prev, emptyTableWidget()]);
  const addTabsWidget = () => setWidgets((prev) => [...prev, emptyTabsWidget()]);
  const addDetailWidget = () => setWidgets((prev) => [...prev, emptyDetailWidget()]);
  const removeWidget = (index: number) =>
    setWidgets((prev) => prev.filter((_, i) => i !== index));
  const replaceWidget = (index: number, next: Widget) =>
    setWidgets((prev) => prev.map((w, i) => (i === index ? next : w)));
  const moveWidget = (index: number, delta: number) =>
    setWidgets((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const copy = [...prev];
      const [item] = copy.splice(index, 1);
      copy.splice(target, 0, item);
      return copy;
    });

  // --- Tabs card helpers (operate on a single TabsWidget value) -----------

  const updateTabsTabs = (widgetIndex: number, tabs: TabEntry[]) =>
    replaceWidget(widgetIndex, { type: "tabs", tabs });

  const addTab = (widgetIndex: number, tabs: TabEntry[]) =>
    updateTabsTabs(widgetIndex, [...tabs, { title: "", widget: emptyTableWidget() }]);
  const removeTab = (widgetIndex: number, tabs: TabEntry[], tabIndex: number) =>
    updateTabsTabs(widgetIndex, tabs.filter((_, i) => i !== tabIndex));
  const setTabTitle = (widgetIndex: number, tabs: TabEntry[], tabIndex: number, title: string) =>
    updateTabsTabs(widgetIndex, tabs.map((t, i) => (i === tabIndex ? { ...t, title } : t)));
  const setTabWidget = (widgetIndex: number, tabs: TabEntry[], tabIndex: number, widget: Widget) =>
    updateTabsTabs(widgetIndex, tabs.map((t, i) => (i === tabIndex ? { ...t, widget } : t)));
  // Switch a tab's child widget TYPE (table <-> detail). Replaces the tab's widget with a
  // fresh empty widget of the chosen type; same-type is a no-op (preserves existing edits).
  const setTabWidgetType = (
    widgetIndex: number,
    tabs: TabEntry[],
    tabIndex: number,
    type: "table" | "detail"
  ) => {
    const current = tabs[tabIndex]?.widget;
    if (current && current.type === type) return;
    const widget: Widget = type === "table" ? emptyTableWidget() : emptyDetailWidget();
    setTabWidget(widgetIndex, tabs, tabIndex, widget);
  };
  const moveTab = (widgetIndex: number, tabs: TabEntry[], tabIndex: number, delta: number) => {
    const target = tabIndex + delta;
    if (target < 0 || target >= tabs.length) return;
    const copy = [...tabs];
    const [item] = copy.splice(tabIndex, 1);
    copy.splice(target, 0, item);
    updateTabsTabs(widgetIndex, copy);
  };

  // --- Save ---------------------------------------------------------------

  const handleSubmit = async () => {
    setFormError(null);

    if (!title.trim()) {
      setFormError("Title is required.");
      return;
    }
    if (!slug.trim()) {
      setFormError("Slug is required.");
      return;
    }

    // The view JSON string is the source of truth. If the builder is active, serialise its
    // widget list into the string first so the latest edits are saved.
    let rawToSave = viewJson;
    if (mode === "builder" && !builderParseError) {
      rawToSave = serializeView(buildViewFromBuilder(widgets, params));
      setViewJson(rawToSave);
    }

    const parsed = parseView(rawToSave);
    if (!parsed.ok) {
      setFormError(`The view JSON is invalid: ${parsed.error}`);
      return;
    }

    setSaving(true);
    try {
      const body = JSON.stringify({
        data: { title: title.trim(), slug: slug.trim(), view: parsed.view },
      });
      const res = isEdit
        ? await apiFetch(`/pages/${id}`, { method: "PUT", body })
        : await apiFetch(`/pages`, { method: "POST", body });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error?.message || `Save failed: ${res.status}`);
      }
      navigate("/pages");
    } catch (e) {
      setFormError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    );
  }

  if (loadError) {
    return (
      <ContentLayout header={<Header variant="h1">Edit page</Header>}>
        <Alert type="error" header="This page cannot be loaded">
          {loadError}
        </Alert>
      </ContentLayout>
    );
  }

  // --- Builder: a single widget card --------------------------------------
  const renderWidgetCard = (widget: Widget, index: number) => {
    const typeLabel =
      widget.type === "table"
        ? "Table widget"
        : widget.type === "tabs"
        ? "Tabs widget"
        : widget.type === "detail"
        ? "Detail widget"
        : `Unknown (${(widget as { type: string }).type})`;
    return (
      <Container
        key={`widget-${index}`}
        header={
          <Header
            variant="h3"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  iconName="angle-up"
                  variant="icon"
                  ariaLabel={`Move widget ${index + 1} up`}
                  disabled={index === 0}
                  onClick={() => moveWidget(index, -1)}
                />
                <Button
                  iconName="angle-down"
                  variant="icon"
                  ariaLabel={`Move widget ${index + 1} down`}
                  disabled={index === widgets.length - 1}
                  onClick={() => moveWidget(index, 1)}
                />
                <Button
                  iconName="close"
                  variant="icon"
                  ariaLabel={`Remove widget ${index + 1}`}
                  onClick={() => removeWidget(index)}
                />
              </SpaceBetween>
            }
          >
            {index + 1}. {typeLabel}
          </Header>
        }
      >
        {widget.type === "table" ? (
          <TableWidgetConfig
            value={widget as TableWidget}
            schema={schema}
            paramNames={paramNames}
            onChange={(next) => replaceWidget(index, next)}
          />
        ) : widget.type === "detail" ? (
          <DetailWidgetConfig
            value={widget as DetailWidget}
            schema={schema}
            paramNames={paramNames}
            onChange={(next) => replaceWidget(index, next)}
          />
        ) : widget.type === "tabs" ? (
          renderTabsCardBody(widget as TabsWidget, index)
        ) : (
          <Alert type="warning" header="Unsupported widget">
            This widget type is not editable in the builder. Switch to JSON to edit it.
          </Alert>
        )}
      </Container>
    );
  };

  // --- Builder: the body of a Tabs card (ordered tabs, each a table sub-form) ---------
  const renderTabsCardBody = (tabsWidget: TabsWidget, widgetIndex: number) => {
    const tabs = tabsWidget.tabs;
    return (
      <SpaceBetween size="l">
        <Box color="text-body-secondary" fontSize="body-s">
          Each tab contains a table or a detail widget. (Nested tabs are supported in JSON but not in the builder.)
        </Box>
        {tabs.length === 0 && <Box color="text-body-secondary">No tabs yet.</Box>}
        {tabs.map((tab, tabIndex) => (
          <Container
            key={`tab-${widgetIndex}-${tabIndex}`}
            header={
              <Header
                variant="h3"
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      iconName="angle-up"
                      variant="icon"
                      ariaLabel={`Move tab ${tabIndex + 1} up`}
                      disabled={tabIndex === 0}
                      onClick={() => moveTab(widgetIndex, tabs, tabIndex, -1)}
                    />
                    <Button
                      iconName="angle-down"
                      variant="icon"
                      ariaLabel={`Move tab ${tabIndex + 1} down`}
                      disabled={tabIndex === tabs.length - 1}
                      onClick={() => moveTab(widgetIndex, tabs, tabIndex, 1)}
                    />
                    <Button
                      iconName="close"
                      variant="icon"
                      ariaLabel={`Remove tab ${tabIndex + 1}`}
                      onClick={() => removeTab(widgetIndex, tabs, tabIndex)}
                    />
                  </SpaceBetween>
                }
              >
                Tab {tabIndex + 1}
              </Header>
            }
          >
            <SpaceBetween size="m">
              <FormField label="Tab title" description="The label shown on the tab.">
                <Input
                  value={tab.title}
                  placeholder="Tab title"
                  onChange={({ detail }) => setTabTitle(widgetIndex, tabs, tabIndex, detail.value)}
                />
              </FormField>
              {/* A tab's content is a table or a detail widget in the builder. */}
              <FormField
                label="Content type"
                description="Choose whether this tab renders a table or a single-record detail view."
              >
                <SegmentedControl
                  selectedId={tab.widget.type === "detail" ? "detail" : "table"}
                  onChange={({ detail }) =>
                    setTabWidgetType(widgetIndex, tabs, tabIndex, detail.selectedId as "table" | "detail")
                  }
                  options={[
                    { id: "table", text: "Table" },
                    { id: "detail", text: "Detail" },
                  ]}
                />
              </FormField>
              {tab.widget.type === "detail" ? (
                <DetailWidgetConfig
                  value={tab.widget as DetailWidget}
                  schema={schema}
                  paramNames={paramNames}
                  onChange={(next) => setTabWidget(widgetIndex, tabs, tabIndex, next)}
                />
              ) : tab.widget.type === "table" ? (
                <TableWidgetConfig
                  value={tab.widget as TableWidget}
                  schema={schema}
                  paramNames={paramNames}
                  onChange={(next) => setTabWidget(widgetIndex, tabs, tabIndex, next)}
                />
              ) : (
                <Alert type="warning" header="Unsupported tab content">
                  This tab's widget type is not editable in the builder. Switch to JSON to edit it.
                </Alert>
              )}
            </SpaceBetween>
          </Container>
        ))}
        <Button iconName="add-plus" onClick={() => addTab(widgetIndex, tabs)}>
          Add tab
        </Button>
      </SpaceBetween>
    );
  };

  return (
    <ContentLayout header={<Header variant="h1">{isEdit ? "Edit page" : "Create page"}</Header>}>
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => navigate("/pages")} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={saving}>
              {isEdit ? "Save changes" : "Create page"}
            </Button>
          </SpaceBetween>
        }
        errorText={formError ?? undefined}
      >
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">Page details</Header>}>
            <SpaceBetween size="m">
              <FormField label="Title" description="The display name of the page.">
                <Input value={title} onChange={({ detail }) => setTitle(detail.value)} />
              </FormField>
              <FormField label="Slug" description="URL path segment. The page renders at /p/<slug>.">
                <Input value={slug} onChange={({ detail }) => setSlug(detail.value)} />
              </FormField>
            </SpaceBetween>
          </Container>

          <Container
            header={
              <Header
                variant="h2"
                description="Define the widgets this page renders, in order. The JSON is the source of truth; the builder is a convenience."
                actions={
                  <SegmentedControl
                    selectedId={mode}
                    onChange={({ detail }) => onModeChange(detail.selectedId as Mode)}
                    options={[
                      { id: "json", text: "JSON" },
                      { id: "builder", text: "Builder" },
                    ]}
                  />
                }
              >
                View
              </Header>
            }
          >
            {mode === "json" ? (
              <FormField label="View JSON" description="The raw view document stored on the page.">
                <Textarea
                  value={viewJson}
                  onChange={({ detail }) => setViewJson(detail.value)}
                  rows={20}
                  spellcheck={false}
                />
              </FormField>
            ) : builderParseError ? (
              <Alert type="error" header="Cannot open the builder">
                The current view JSON could not be parsed, so the builder form is hidden to avoid
                losing your text. Switch back to JSON to fix it. Error: {builderParseError}
              </Alert>
            ) : (
              <SpaceBetween size="l">
                {schemaError && (
                  <Alert type="error" header="Failed to load content-type schema">
                    {schemaError}
                  </Alert>
                )}

                <Container
                  header={
                    <Header
                      variant="h3"
                      description="Named URL query parameters this page accepts (e.g. ?instanceid=...). Parameters are inert until a table's 'Parameter filters' binds a field to one. Names must be unique."
                    >
                      Parameters
                    </Header>
                  }
                >
                  <SpaceBetween size="s">
                    {params.length === 0 && (
                      <Box color="text-body-secondary">
                        No parameters yet. Add one to let table filters bind to it.
                      </Box>
                    )}
                    {params.map((param, index) => {
                      const duplicate = isDuplicateParamName(index);
                      return (
                        <div key={`param-${index}`}>
                          <SpaceBetween direction="horizontal" size="xs">
                            <FormField
                              errorText={duplicate ? "Duplicate parameter name." : undefined}
                            >
                              <Input
                                value={param.name}
                                placeholder="Parameter name (e.g. instanceid)"
                                onChange={({ detail }) => setParamName(index, detail.value)}
                              />
                            </FormField>
                            <Button
                              iconName="close"
                              variant="icon"
                              ariaLabel={`Remove parameter ${index + 1}`}
                              onClick={() => removeParam(index)}
                            />
                          </SpaceBetween>
                        </div>
                      );
                    })}
                    <Button iconName="add-plus" onClick={addParam}>
                      Add parameter
                    </Button>
                  </SpaceBetween>
                </Container>

                {widgets.length === 0 && (
                  <Box color="text-body-secondary">
                    No widgets yet. Add a Table or Tabs widget to start building the page.
                  </Box>
                )}

                {widgets.map((widget, index) => renderWidgetCard(widget, index))}

                <SpaceBetween direction="horizontal" size="xs">
                  <Button iconName="add-plus" onClick={addTableWidget}>
                    Add table widget
                  </Button>
                  <Button iconName="add-plus" onClick={addTabsWidget}>
                    Add tabs widget
                  </Button>
                  <Button iconName="add-plus" onClick={addDetailWidget}>
                    Add detail widget
                  </Button>
                </SpaceBetween>
              </SpaceBetween>
            )}
          </Container>
        </SpaceBetween>
      </Form>
    </ContentLayout>
  );
}
